import md5 from 'md5';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';

import { components, httpClient, useUser } from '@work4all/data';

import {
  PasswordChangeDialog,
  PasswordChangeDialogOptions,
  PasswordData,
} from './PasswordChangeDialog';

interface Props extends PasswordChangeDialogOptions {
  userId: number;
}

export const usePasswordChangeDialog = ({
  userId,
  hideOldPassword,
  hideLoggingOutWarning,
}: Props) => {
  const dialogs = useDialogs();
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation();

  const currentUser = useUser();

  const [loading, setLoading] = useState(false);

  const performPasswordChange = useCallback(
    async (data: PasswordData) => {
      const body = {
        benutzerCode: userId,
        newPasswordHash: md5(data.newPassword),
      };

      if (!hideOldPassword) {
        body['oldPasswordHash'] = md5(data.oldPassword);
      }

      setLoading(true);

      try {
        const response = await httpClient.put<
          components['schemas']['DefaultResponse'],
          components['schemas']['ChangePasswordRequest']
        >({
          url: `${currentUser.baseUrl}/api/work4all/benutzer/ChangePassword`,
          body,
          contentType: 'application/json',
          headers: {
            'x-work4all-apiurl': currentUser.baseUrl,
            Authorization: `Bearer ${currentUser.token}`,
          },
        });

        return response.data;
      } catch (error) {
        return { ok: false, message: error };
      } finally {
        setLoading(false);
      }
    },
    [currentUser.baseUrl, currentUser.token, userId, hideOldPassword]
  );

  return useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        const onClose = () => {
          resolve(false);
          dialogs.close(id);
        };

        const onConfirm = async (
          data: PasswordData,
          setError: UseFormSetError<PasswordData>
        ) => {
          const { ok, message } = await performPasswordChange(data);

          if (ok) {
            resolve(true);
            dialogs.close(id);
          } else if (
            message === 'Das angegebene aktuelle Passwort ist nicht korrekt.'
          ) {
            setError('oldPassword', {
              type: 'manual',
              message: t('ALERTS.PASSWORD_INCORRECT'),
            });
          } else {
            enqueueSnackbar(t('ALERTS.ERROR.UNKNOWN'), {
              variant: 'error',
              autoHideDuration: 3000,
            });
          }
        };

        const { id } = dialogs.open(PasswordChangeDialog, {
          onConfirm,
          onClose,
          loading,
          options: {
            hideOldPassword,
            hideLoggingOutWarning,
          },
        });
      }),
    [
      dialogs,
      enqueueSnackbar,
      loading,
      performPasswordChange,
      t,
      hideOldPassword,
      hideLoggingOutWarning,
    ]
  );
};
